import {
  AHButton,
  AHIcon,
  AHInput,
  AHSeparator,
  AHText,
  Box,
  ListItem,
  useBoxStyle,
} from "arkh";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";
import { HeaderButton } from "../../components";
import {
  StoreAdminNavigationProp,
  StoreAdminNavigationScreenProps,
} from "../StoreAdmin/StoreAdminNavigation";
import { FormattedMessage, useIntl } from "react-intl";
import {
  TeamSelectList,
  TeamSelectListWholeStoreOption,
} from "widgets/src/TeamSelectList";
import { IntegrationTomraScreenTeamsQuery } from "./__generated__/IntegrationTomraScreenTeamsQuery.graphql";
import { Alert, FlatList } from "react-native";
import { IntegrationTomraScreenRvmsQuery } from "./__generated__/IntegrationTomraScreenRvmsQuery.graphql";
import { IntegrationTomraScreen_RvmFragment$key } from "./__generated__/IntegrationTomraScreen_RvmFragment.graphql";
import {
  ListSectionHeader,
  ListSectionHeaderTitle,
} from "../../components/atoms/ListSectionHeader";
import { useIsStoresprintAdmin } from "../../useHasFeatureFlag";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

export function IntegrationTomraScreen(
  props: StoreAdminNavigationScreenProps<"IntegrationTomra">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerIntegrationTomraScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerIntegrationTomraScreen(
  props: StoreAdminNavigationScreenProps<"IntegrationTomra"> & {
    fetchKey: string;
  }
) {
  const isAdmin = useIsStoresprintAdmin();

  return (
    <Box space="medium">
      <ListSectionHeader>
        <ListSectionHeaderTitle spaceBottom="small">
          <FormattedMessage
            description="IntegrationTomra teams title"
            defaultMessage="Assign Teams"
          />
        </ListSectionHeaderTitle>
      </ListSectionHeader>
      <ListItem
        isFirst
        isLast
        onPress={() =>
          props.navigation.navigateDeprecated("IntegrationTomraTeams", {})
        }
        left={<AHIcon type="team" />}
        right={<ListItem.NavArrow />}
      >
        <ListItem.Title>
          <FormattedMessage
            description="IntegrationTomraTeams title"
            defaultMessage="Teams"
          />
        </ListItem.Title>
      </ListItem>
      <ListSectionHeader>
        <ListSectionHeaderTitle spaceTop="large" spaceBottom="small">
          <FormattedMessage
            description="IntegrationTomra rvms title"
            defaultMessage="Change Machine Names"
          />
        </ListSectionHeaderTitle>
      </ListSectionHeader>
      <ListItem
        isFirst
        isLast
        onPress={() =>
          props.navigation.navigateDeprecated("IntegrationTomraRvms", {})
        }
        left={<AHIcon type="rvm" />}
        right={<ListItem.NavArrow />}
      >
        <ListItem.Title>
          <FormattedMessage
            description="IntegrationTomraRvms title"
            defaultMessage="RVMs"
          />
        </ListItem.Title>
      </ListItem>
      {isAdmin ? (
        <>
          <ListSectionHeader>
            <ListSectionHeaderTitle spaceTop="large" spaceBottom="small">
              Deactivate Integration
            </ListSectionHeaderTitle>
          </ListSectionHeader>
          <DeactivateButton {...props} />
        </>
      ) : null}
    </Box>
  );
}

export function IntegrationTomraTeamsScreen(
  props: StoreAdminNavigationScreenProps<"IntegrationTomraTeams">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerIntegrationTomraTeamsScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

const InnerIntegrationTomraTeamsScreen = (props: {
  fetchKey: string;
  navigation: StoreAdminNavigationProp<"IntegrationTomra">;
}) => {
  const [selectedTeams, setSelectedTeams] = useState<string[]>();

  const data = useLazyLoadQuery<IntegrationTomraScreenTeamsQuery>(
    graphql`
      query IntegrationTomraScreenTeamsQuery {
        store {
          publicTeams {
            ...TeamSelectList_Team
          }
          integrationTomra {
            id
            teamIds
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
    }
  );

  const [setTeamIds, loading] = useMutation(graphql`
    mutation IntegrationTomraScreenMutation($teamIds: [ID!]!) {
      store {
        setTomraTeamIds(teamIds: $teamIds) {
          id
          teamIds
        }
      }
    }
  `);

  useEffect(() => {
    if (
      selectedTeams === undefined &&
      data.store.integrationTomra?.teamIds != null
    ) {
      setSelectedTeams([...data.store.integrationTomra.teamIds]);
    }
  }, [selectedTeams, data.store.integrationTomra]);

  const onSave = useCallback(() => {
    setTeamIds({
      variables: {
        teamIds: selectedTeams,
      },
      onCompleted: () => {
        props.navigation.goBack();
      },
    });
  }, [setTeamIds, selectedTeams, props.navigation]);

  useLayoutEffect(() => {
    const changed =
      selectedTeams !== undefined &&
      (selectedTeams.length !== data.store.integrationTomra?.teamIds?.length ||
        selectedTeams.find(
          (selectedTeamId) =>
            !data.store.integrationTomra?.teamIds?.find(
              (teamId) => teamId === selectedTeamId
            )
        ) !== undefined);

    props.navigation.setOptions({
      headerRight: () => (
        <HeaderButton
          nativeHeader
          type="SAVE"
          onPress={onSave}
          disabled={!changed || loading}
        />
      ),
    });
  }, [
    props.navigation,
    selectedTeams,
    data.store.integrationTomra?.teamIds,
    onSave,
    loading,
  ]);

  return (
    <TeamSelectList
      insets={undefined}
      disabled={() => selectedTeams === undefined}
      teams={data.store.publicTeams}
      selected={selectedTeams ?? []}
      onItemPress={(teamId, selected) => {
        if (selected) {
          setSelectedTeams(
            selectedTeams?.filter((selectedTeamId) => selectedTeamId !== teamId)
          );
        } else {
          setSelectedTeams([...(selectedTeams ?? []), teamId]);
        }
      }}
      ListHeaderComponent={
        <>
          <AHText
            type="title-small"
            spaceBottom="medium"
            spaceHorizontal="medium"
            spaceTop="medium"
          >
            <FormattedMessage
              description="IntegrationTomraTeams header"
              defaultMessage="Choose teams to assign to issues."
            />
          </AHText>
          <TeamSelectListWholeStoreOption
            onSelect={() => {
              if (selectedTeams !== undefined) {
                setSelectedTeams([]);
              }
            }}
            selected={selectedTeams?.length === 0}
          />
        </>
      }
    />
  );
};

export function IntegrationTomraRvmsScreen(
  props: StoreAdminNavigationScreenProps<"IntegrationTomraRvms">
) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerIntegrationTomraRvmsScreen {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

const InnerIntegrationTomraRvmsScreen = (
  props: StoreAdminNavigationScreenProps<"IntegrationTomraRvms"> & {
    fetchKey: string;
  }
) => {
  const data = useLazyLoadQuery<IntegrationTomraScreenRvmsQuery>(
    graphql`
      query IntegrationTomraScreenRvmsQuery {
        store {
          integrationTomra {
            rvms {
              id
              name
              ...IntegrationTomraScreen_RvmFragment
            }
          }
        }
      }
    `,
    {},
    {
      fetchKey: props.fetchKey,
    }
  );

  const [saveNames] = useMutation(graphql`
    mutation IntegrationTomraScreenSetNamesMutation(
      $names: [RenameTomraRvmInput!]!
    ) {
      store {
        setTomraRvmNames(names: $names) {
          id
          name
        }
      }
    }
  `);

  const [names, setNames] = useState(
    Object.fromEntries(
      data.store.integrationTomra?.rvms?.map((rvm) => [rvm.id, rvm.name]) ?? []
    )
  );

  const onNameChanged = useCallback(
    (id: string, newName: string | null) => {
      setNames({ ...names, [id]: newName });
    },
    [names]
  );

  const onSave = useCallback(() => {
    saveNames({
      variables: {
        names: Object.entries(names).map(([id, name]) => ({
          id,
          name,
        })),
      },
      onCompleted: () => props.navigation.goBack(),
    });
  }, [saveNames, names, props.navigation]);

  useLayoutEffect(() => {
    const changed =
      Object.entries(names).find(
        (e) =>
          data.store.integrationTomra?.rvms?.find(
            (rvm) => rvm.id === e[0] && rvm.name !== e[1]
          ) !== undefined
      ) !== undefined;

    props.navigation.setOptions({
      headerRight: () => (
        <HeaderButton type="SAVE" onPress={onSave} disabled={!changed} />
      ),
    });
  }, [names, props.navigation, onSave, data.store.integrationTomra?.rvms]);

  const style = useBoxStyle({
    space: "medium",
  });

  return (
    <FlatList
      data={data.store.integrationTomra?.rvms}
      renderItem={({ item, index }) => (
        <RvmListItem
          rvmKey={item}
          name={names[item.id!]}
          setName={(newName) => onNameChanged(item.id!, newName)}
          isFirst={index === 0}
        />
      )}
      ItemSeparatorComponent={() => <AHSeparator />}
      renderScrollComponent={(props) => <KeyboardAwareScrollView {...props} />}
      contentContainerStyle={style}
      ListHeaderComponent={
        <>
          <ListSectionHeader>
            <ListSectionHeaderTitle>
              <FormattedMessage
                description="IntegrationTomraRvms change names title"
                defaultMessage="Change Names"
              />
            </ListSectionHeaderTitle>
          </ListSectionHeader>
        </>
      }
    />
  );
};

const RvmListItem = ({
  rvmKey,
  name,
  setName,
  isFirst,
}: {
  rvmKey: IntegrationTomraScreen_RvmFragment$key;
  name: string | null;
  setName: (newName: string) => void;
  isFirst?: boolean;
}) => {
  const rvm = useFragment(
    graphql`
      fragment IntegrationTomraScreen_RvmFragment on IntegrationTomraRVM {
        id
        serialNumber
        name
      }
    `,
    rvmKey
  );

  const intl = useIntl();

  return (
    <Box>
      <AHText
        spaceTop={isFirst ? "small" : "large"}
        spaceBottom="small"
        spaceHorizontal="medium"
      >
        <FormattedMessage
          description="IntegrationTomraRvms serial number"
          defaultMessage="Serial Number: {serialNumber}"
          values={{
            serialNumber: rvm.serialNumber,
          }}
        />
      </AHText>
      <AHInput
        color="surface"
        space="medium"
        cornerRadius="large"
        value={name ?? ""}
        onChangeText={setName}
        placeholder={intl.formatMessage({
          description: "IntegrationTomraRvms Name",
          defaultMessage: "Name",
        })}
      />
    </Box>
  );
};

const DeactivateButton = (
  props: StoreAdminNavigationScreenProps<"IntegrationTomra">
) => {
  const [deactivate, loading] = useMutation(graphql`
    mutation IntegrationTomraScreenDecativateMutation {
      store {
        deactivateIntegrationTomra
      }
    }
  `);

  const onDeactivate = useCallback(() => {
    deactivate({
      variables: {},
      onCompleted: () => props.navigation.goBack(),
      onError: () => Alert.alert("Error"),
    });
  }, [deactivate, props.navigation]);

  return (
    <AHButton
      themeType="negative"
      onPress={onDeactivate}
      title="Deactivate"
      loading={loading}
    />
  );
};
